import { Capacitor } from '@capacitor/core'
import { setupPWAKeyboardFix, isIosStandalonePwa } from './pwaKeyboardFix.ts'

/** Evita che Invia (o altri bottoni) rubino il focus — iOS chiude la tastiera. */
export function preventButtonFocusSteal(button: HTMLElement): void {
  button.addEventListener('mousedown', (e) => {
    e.preventDefault()
  })
}

/** Mantieni la tastiera aperta dopo invio messaggio (iPad/iPhone). */
export function keepComposerInputFocused(input: HTMLElement): void {
  input.focus({ preventScroll: true })
}

/**
 * Fix freeze WebKit su iOS PWA (layout fixed): niente scroll su focus;
 * su blur solo scroll istantaneo della window, senza timer.
 */
export function attachComposerInputIosKeyboardFix(input: HTMLElement): void {
  input.addEventListener('blur', () => {
    window.scrollTo(0, 0)
  })
}

function scrollMessagesToBottom(messages: HTMLElement | null, smooth = false): void {
  if (!messages) return
  const top = messages.scrollHeight
  if (smooth) {
    messages.scrollTo({ top, behavior: 'smooth' })
  } else {
    messages.scrollTop = top
  }
}

function isNativeCapacitor(): boolean {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

/**
 * Capacitor iOS/Android: solleva la chat-input-bar sopra la tastiera nativa
 * (incollata al bordo superiore, senza gap).
 */
async function attachCapacitorKeyboardLift(opts: {
  footer: HTMLElement
  messages?: HTMLElement | null
  input?: HTMLElement | null
}): Promise<() => void> {
  if (!isNativeCapacitor()) return () => {}

  const { Keyboard } = await import('@capacitor/keyboard')
  const root = document.documentElement
  const footer = opts.footer
  const messages = opts.messages ?? null
  const input = opts.input ?? null

  root.classList.add('cap-keyboard-lift')
  footer.classList.add('chat-input-bar--kb')

  const applyLift = (keyboardHeight: number) => {
    const h = Math.max(0, Math.round(keyboardHeight))
    // 2px di margine: barra appena sopra il bordo superiore della tastiera iOS
    const lift = h > 0 ? Math.max(0, h - 2) : 0
    root.classList.toggle('keyboard-open', lift > 0)
    root.style.setProperty('--keyboard-inset', `${lift}px`)
    footer.style.transform = lift > 0 ? `translateY(-${lift}px)` : 'translateY(0px)'
    // Con tastiera aperta la safe-area inferiore è già coperta dalla tastiera
    if (lift > 0) {
      footer.style.paddingBottom = '8px'
    } else {
      footer.style.removeProperty('padding-bottom')
    }
    if (input && document.activeElement === input) {
      scrollMessagesToBottom(messages, false)
    }
  }

  const showHandle = await Keyboard.addListener('keyboardWillShow', (info) => {
    applyLift(info.keyboardHeight)
    window.setTimeout(() => scrollMessagesToBottom(messages, true), 200)
  })
  const showHandleDid = await Keyboard.addListener('keyboardDidShow', (info) => {
    applyLift(info.keyboardHeight)
    scrollMessagesToBottom(messages, false)
  })
  const hideHandle = await Keyboard.addListener('keyboardWillHide', () => {
    applyLift(0)
  })
  const hideHandleDid = await Keyboard.addListener('keyboardDidHide', () => {
    applyLift(0)
  })

  const onFocus = () => {
    window.setTimeout(() => scrollMessagesToBottom(messages, true), 200)
  }
  input?.addEventListener('focus', onFocus)

  return () => {
    void showHandle.remove()
    void showHandleDid.remove()
    void hideHandle.remove()
    void hideHandleDid.remove()
    input?.removeEventListener('focus', onFocus)
    applyLift(0)
    footer.style.removeProperty('transform')
    footer.style.removeProperty('padding-bottom')
    footer.classList.remove('chat-input-bar--kb')
    root.classList.remove('cap-keyboard-lift')
    root.classList.remove('keyboard-open')
    root.style.removeProperty('--keyboard-inset')
  }
}

export function attachChatVisualViewportInsets(opts: {
  app?: HTMLElement | null
  footer: HTMLElement
  messages?: HTMLElement | null
  input?: HTMLElement | null
}): () => void {
  const cleanups: Array<() => void> = []
  let disposed = false

  if (isNativeCapacitor()) {
    void attachCapacitorKeyboardLift({
      footer: opts.footer,
      messages: opts.messages,
      input: opts.input,
    }).then((cleanup) => {
      if (disposed) cleanup()
      else cleanups.push(cleanup)
    })
    return () => {
      disposed = true
      for (const c of cleanups) c()
    }
  }

  if (isIosStandalonePwa()) {
    const cleanup = setupPWAKeyboardFix({
      inputBar: opts.footer,
      inputElement: opts.input,
      messages: opts.messages,
    })
    return () => {
      disposed = true
      cleanup()
    }
  }

  const app =
    opts.app ??
    (document.querySelector('#app.screen-chat') as HTMLElement | null) ??
    (document.querySelector('#app') as HTMLElement | null)
  const messages = opts.messages ?? null
  const input = opts.input ?? null
  const root = document.documentElement
  const vv = window.visualViewport

  let raf = 0
  const focusTimers: number[] = []

  const clearChatVh = () => {
    root.style.removeProperty('--chat-vh')
    root.style.removeProperty('--keyboard-inset')
    root.style.removeProperty('--vv-height')
    root.style.removeProperty('--vv-offset-top')
    root.classList.remove('keyboard-open')
    if (app) {
      app.classList.remove('vv-keyboard-open')
      app.style.removeProperty('height')
      app.style.removeProperty('max-height')
      app.style.removeProperty('top')
      app.style.removeProperty('position')
      app.style.removeProperty('left')
      app.style.removeProperty('right')
      app.style.removeProperty('width')
      app.style.removeProperty('transform')
    }
  }

  const sync = () => {
    if (raf) cancelAnimationFrame(raf)
    raf = requestAnimationFrame(() => {
      raf = 0
      const layoutH = window.innerHeight || document.documentElement.clientHeight
      const vvH = vv?.height ?? layoutH
      const offsetTop = vv?.offsetTop ?? 0
      const inset = Math.max(0, Math.round(layoutH - vvH - offsetTop))
      const focused = input != null && document.activeElement === input
      const keyboardLikelyOpen = focused && inset > 40

      root.style.setProperty('--keyboard-inset', `${inset}px`)
      root.style.setProperty('--vv-height', `${Math.round(vvH)}px`)
      root.style.setProperty('--vv-offset-top', `${Math.round(offsetTop)}px`)

      if (!keyboardLikelyOpen) {
        clearChatVh()
        if (focused) scrollMessagesToBottom(messages, false)
        return
      }

      // Web tab: footer fixed in fondo — solo alza con tastiera, non pin #app
      if (root.classList.contains('web-browser')) {
        root.classList.add('keyboard-open')
        root.style.setProperty('--chat-vh', `${Math.round(vvH)}px`)
        if (focused) scrollMessagesToBottom(messages, false)
        return
      }

      root.classList.add('keyboard-open')
      root.style.setProperty('--chat-vh', `${Math.round(vvH)}px`)
      if (app) {
        app.classList.add('vv-keyboard-open')
        app.style.position = 'fixed'
        app.style.left = '0'
        app.style.right = '0'
        app.style.width = '100%'
        app.style.top = `${Math.round(offsetTop)}px`
        app.style.height = `${Math.round(vvH)}px`
        app.style.maxHeight = `${Math.round(vvH)}px`
        app.style.transform = 'none'
      }

      if (focused) scrollMessagesToBottom(messages, false)
    })
  }

  const onFocus = () => {
    sync()
    for (const ms of [50, 200, 350, 500]) {
      focusTimers.push(
        window.setTimeout(() => {
          sync()
          scrollMessagesToBottom(messages, ms >= 200)
        }, ms),
      )
    }
  }

  const onBlur = () => {
    for (const id of focusTimers) window.clearTimeout(id)
    focusTimers.length = 0
    window.setTimeout(() => {
      if (input && document.activeElement === input) return
      clearChatVh()
      window.scrollTo(0, 0)
    }, 80)
  }

  if (vv) {
    vv.addEventListener('resize', sync)
    vv.addEventListener('scroll', sync)
  }
  window.addEventListener('resize', sync)
  input?.addEventListener('focus', onFocus)
  input?.addEventListener('blur', onBlur)
  sync()

  return () => {
    if (raf) cancelAnimationFrame(raf)
    for (const id of focusTimers) window.clearTimeout(id)
    if (vv) {
      vv.removeEventListener('resize', sync)
      vv.removeEventListener('scroll', sync)
    }
    window.removeEventListener('resize', sync)
    input?.removeEventListener('focus', onFocus)
    input?.removeEventListener('blur', onBlur)
    clearChatVh()
  }
}
